/**
 * SymbologyPanel — Apply simple / advanced symbology to the active layer
 * Updates Redux immediately so Map.tsx re-styles vector features.
 */
import React, { useState, useCallback } from 'react';
import { Box, Typography, Button, Divider, Chip, Stack } from '@mui/material';
import { Palette, AutoAwesome, Visibility } from '@mui/icons-material';
import { tokens } from '../../../theme';
import { useGeoveraTheme } from '../../../theme/ThemeContext';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import {
  updateLayerSymbology,
  updateSymbologyAsync,
  setLayerOpacity,
} from '../../../store/slices/layersSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import SymbologyEditor from '../../SymbologyEditor';
import AdvancedSymbologyEditor from '../../AdvancedSymbologyEditor';
import type { Symbology } from '../../../types/symbology';
import { getDefaultSymbology } from '../../../types/symbology';

const SymbologyPanel: React.FC = () => {
  const { palette } = useGeoveraTheme();
  const dispatch = useAppDispatch();
  const activeLayerId = useAppSelector((s) => s.layers.activeLayerId);
  const layers = useAppSelector((s) => s.layers.layers);
  const activeLayer = layers.find((l) => l.id === activeLayerId);
  const [simpleOpen, setSimpleOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const applySymbology = useCallback(
    (symbology: Symbology) => {
      if (!activeLayer) return;
      // Immediate map refresh via Redux (Map.tsx watches layer.symbology)
      dispatch(updateLayerSymbology({ id: activeLayer.id, symbology }));
      if (typeof symbology.opacity === 'number') {
        dispatch(setLayerOpacity({ id: activeLayer.id, opacity: symbology.opacity }));
      }
      // Best-effort server persist
      void dispatch(updateSymbologyAsync({ layerId: activeLayer.id, symbology }))
        .unwrap()
        .catch(() => {
          /* local apply already done */
        });
      dispatch(
        addNotification({
          type: 'success',
          message: `Style applied to “${activeLayer.name}”`,
        })
      );
    },
    [activeLayer, dispatch]
  );

  if (!activeLayer) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 2,
          gap: 1.5,
          color: palette.text.muted,
        }}
      >
        <Palette sx={{ fontSize: 40, opacity: 0.3 }} />
        <Typography sx={{ fontSize: tokens.fontSize.sm, textAlign: 'center' }}>
          Select a vector layer in Layers to edit its symbology
        </Typography>
      </Box>
    );
  }

  if (activeLayer.type === 'raster') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ fontSize: tokens.fontSize.sm, color: palette.text.secondary }}>
          Raster symbology is controlled by the raster source. Select a vector layer for fill/stroke
          styles.
        </Typography>
      </Box>
    );
  }

  const geom = (activeLayer.geometryType as 'Point' | 'LineString' | 'Polygon') || 'Polygon';
  const current = activeLayer.symbology || getDefaultSymbology(geom);

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderRadius: tokens.radius.md,
          backgroundColor: palette.accent.primaryDim,
          border: `1px solid ${palette.accent.primary}30`,
        }}
      >
        <Palette sx={{ fontSize: 16, color: palette.accent.primary }} />
        <Typography
          sx={{
            fontSize: tokens.fontSize.md,
            fontWeight: 600,
            color: palette.accent.primary,
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {activeLayer.name}
        </Typography>
        <Chip label={geom} size="small" sx={{ fontSize: tokens.fontSize.xs, height: 18 }} />
      </Box>

      {/* Live style summary */}
      <Box
        sx={{
          p: 1.25,
          borderRadius: tokens.radius.md,
          border: `1px solid ${palette.border.subtle}`,
          bgcolor: palette.bg.elevated,
        }}
      >
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            color: palette.text.muted,
            textTransform: 'uppercase',
            fontWeight: 700,
            mb: 1,
          }}
        >
          Current style
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: geom === 'Point' ? '50%' : 1,
              bgcolor: current.fillColor || '#3388ff',
              border: `3px solid ${current.strokeColor || '#ffffff'}`,
              opacity: current.opacity ?? 1,
            }}
          />
          <Box>
            <Typography sx={{ fontSize: tokens.fontSize.sm, fontWeight: 600 }}>
              {(current.type || 'simple').toString()}
            </Typography>
            <Typography sx={{ fontSize: tokens.fontSize.xs, color: palette.text.muted }}>
              Fill {current.fillColor || '—'} · Stroke {current.strokeColor || '—'} ·{' '}
              {Math.round((current.opacity ?? 1) * 100)}%
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Button
        variant="contained"
        size="medium"
        startIcon={<Visibility sx={{ fontSize: 18 }} />}
        onClick={() => setSimpleOpen(true)}
        fullWidth
        sx={{ textTransform: 'none', fontWeight: 700 }}
      >
        Edit simple style
      </Button>

      <Divider />

      <Button
        variant="outlined"
        size="small"
        startIcon={<AutoAwesome sx={{ fontSize: 16 }} />}
        onClick={() => setAdvancedOpen(true)}
        fullWidth
        sx={{ fontSize: tokens.fontSize.sm, textTransform: 'none', fontWeight: 600 }}
      >
        Advanced (categorized / graduated)
      </Button>

      <SymbologyEditor
        open={simpleOpen}
        layerName={activeLayer.name}
        geometryType={geom}
        currentSymbology={current}
        onApply={(sym) => {
          applySymbology(sym);
          setSimpleOpen(false);
        }}
        onCancel={() => setSimpleOpen(false)}
      />

      <AdvancedSymbologyEditor
        open={advancedOpen}
        layerName={activeLayer.name}
        geometryType={geom}
        features={activeLayer.features?.map((f) => ({ properties: f.properties || {} })) || []}
        currentSymbology={current}
        onApply={(sym: Symbology) => {
          applySymbology(sym);
          setAdvancedOpen(false);
        }}
        onClose={() => setAdvancedOpen(false)}
      />
    </Box>
  );
};

export default SymbologyPanel;
